// src/components/ui/SlideOver.tsx
// Slide-over panel component for detail views

import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { isTopmostModalDialog } from './topmostDialog';
import { X } from 'lucide-react';
import Button from './Button';

export interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
  footer?: ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  /**
   * Optional CSS length to inset the panel + overlay from the top of the
   * viewport (e.g. to sit BELOW a fixed app header instead of covering it).
   * Any valid CSS `top` value — px, rem, or a calc()/var() expression.
   * Defaults to full-height (top: 0).
   */
  topOffset?: string;
}

const sizeClasses: Record<NonNullable<SlideOverProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export default function SlideOver({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  topOffset,
}: SlideOverProps) {
  const offsetStyle = topOffset ? { top: topOffset } : undefined;
  const panelRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  /**
   * LATEST CALLBACKS, WITHOUT MAKING THEM A LIFECYCLE INPUT.
   *
   * These used to reach the effect below through a `useCallback` keyed on
   * `[onClose, closeOnEscape]`, so the effect's dependencies changed whenever a
   * parent re-rendered with a fresh callback identity -- which is the normal
   * case: `PhonePage` declares `closeDrawer` as a plain function, so every one
   * of its renders produced a new one.
   *
   * Re-running the effect re-runs FOCUS MANAGEMENT. The cleanup restored focus
   * to whatever was focused when the drawer opened, and the setup then pulled
   * focus onto the panel. An open drawer therefore stole focus from its own
   * children every time its parent re-rendered.
   *
   * That is how a card field became untypable. Clicking a cross-origin payment
   * iframe blurs the top window; RTK Query's `refetchOnFocus` refetches when it
   * returns; `PhonePage` re-renders; focus was yanked out of the payment field
   * onto this panel div before a single keystroke arrived. The visible symptom
   * was the focus ring flickering on and straight back off.
   *
   * A drawer should take focus when it OPENS. Not when its parent renders.
   */
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  // Focus management and keyboard handling, per OPEN LIFECYCLE.
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedElement.current = document.activeElement as HTMLElement;

    // Defined here rather than passed in, so the listener is stable for this
    // open lifecycle while still calling whatever the parent last supplied.
    const onKeyDown = (e: KeyboardEvent): void => {
      // Only the topmost modal answers Escape (see topmostDialog.ts): a
      // Modal opened from this panel closes alone.
      if (e.key === 'Escape' && closeOnEscapeRef.current && isTopmostModalDialog(rootRef.current)) {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    // Focus the panel, once for this opening.
    const focusPanel = setTimeout(() => {
      panelRef.current?.focus();
    }, 0);

    return () => {
      // Without this, a drawer closed inside the same tick still focuses a
      // panel that is on its way out.
      clearTimeout(focusPanel);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      previouslyFocusedElement.current?.focus();
    };
    // `isOpen` ONLY. Callback identity is deliberately not a dependency; the
    // refs above keep it current without restarting focus management.
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const slideOverContent = (
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 overflow-hidden"
      style={offsetStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'slideover-title' : undefined}
      aria-describedby={description ? 'slideover-description' : undefined}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Panel container */}
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        {/* Panel */}
        <div
          ref={panelRef}
          tabIndex={-1}
          className={`
            relative w-screen ${sizeClasses[size]}
            transform transition-transform duration-300 ease-in-out
            bg-white shadow-xl
            focus:outline-none
            flex flex-col
          `}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <div>
                {title && (
                  <h2
                    id="slideover-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id="slideover-description"
                    className="mt-1 text-sm text-gray-500"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close panel"
                  className="-mr-2 -mt-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(slideOverContent, document.body);
}
